<template>
  <div>
    <b-card no-body>
      <!-- Head -->
      <table-header
        search
        search-placeholder="Cari di sini"
        search-action="setting/getSettingsList"
        :actions="['filter', 'buttonAdd']"
      >
        <template #filter>
          <v-select
            v-model="categoryId"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            label="name"
            multiple
            :options="articleCategories.rows"
            placeholder="Filter Kategori"
            @input="handleFilterCategory"
          />
        </template>
        <template #buttonAdd>
          <b-button
            v-if="canAccess('manage', 'Article')"
            variant="primary"
            :to="{ name: 'articles-create'}"
          >
            + Buat Artikel
          </b-button>
        </template>
      </table-header>
      <!-- Alert -->
      <div
        v-if="fixedSearchKeyword"
        class="mx-2"
      >
        <b-alert
          variant="primary"
          show
        >
          Menampilkan hasil untuk "{{ fixedSearchKeyword }}"
        </b-alert>
      </div>
      <!-- Body -->
      <b-skeleton-table
        v-if="isLoading"
        :rows="5"
        :columns="4"
      />
      <b-table
        v-else
        ref="refUserListTable"
        :items="articles.data"
        responsive
        :fields="tableFields"
        primary-key="id"
        show-empty
        empty-text="Tidak ada data yang ditemukan"
        class="position-relative mh-160"
      >

        <!-- Column: Thumbnail -->
        <template #cell(thumbnail)="data">
          <b-img
            rounded
            width="50"
            :src="linkStaging(`${data.item.primary_image ? data.item.primary_image : ''}`, 50, 50)"
            :alt="data.item.slug"
            @error="(event) => {
              event.target.src = require('@/assets/images/icons/broken-image.svg')
            }"
          />
        </template>

        <!-- Column: Judul -->
        <template #cell(title)="data">
          {{ data.item.title }}
        </template>

        <!-- Column: Kategori -->
        <template #cell(category)="data">
          <b-badge
            v-for="(item, index) in data.item.category"
            :key="`item-category-${index}`"
            variant="light-primary"
            class="mr-1 mb-1"
          >
            {{ item.name }}
          </b-badge>
        </template>

        <!-- Column: Publikasi -->
        <template #cell(publish)="data">
          <b-form-checkbox
            :checked="!!data.item.is_active"
            name="check-button"
            switch
            inline
            @change="activated(data.item, $event)"
          />
        </template>

        <!-- Column: Dibuat -->
        <template #cell(created_at)="data">
          {{ formatSelectDate(data.item.created_at, ['date','month','year','time']) }}
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <!-- Dropdown -->
            <table-action
              :actions="tableAction"
              :data="data"
            />
          </div>
        </template>

      </b-table>
      <!-- Footer -->
      <div class="mx-2 mb-2">
        <pagination
          :data="articles.meta"
          action="article/getArticles"
        />
      </div>
    </b-card>

    <b-modal
      v-if="canAccess('manage', 'Article')"
      id="modal-delete"
      title="Apakah anda yakin"
      @hidden="deletedData = ''"
    >
      Ingin Menghapus Artikel ini?
      <template #modal-footer="{ cancel }">
        <b-button
          variant="outline-secondary"
          :disabled="isDeleting"
          @click="cancel()"
        >
          Batalkan
        </b-button>
        <b-button
          variant="danger"
          :disabled="isDeleting"
          @click.prevent="handleDelete"
        >
          <b-spinner
            v-if="isDeleting"
            small
            label="Spinning"
          />
          {{ isDeleting ? 'Menghapus' : 'Ya, Hapus' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import {
  BSkeletonTable,
  BAlert,
  BButton,
  BCard,
  BTable,
  VBTooltip,
  BModal,
  BFormCheckbox,
  BBadge,
  BImg,
} from 'bootstrap-vue'
import Pagination from '@/layouts/components/custom/Pagination.vue'
import TableHeader from '@/layouts/components/custom/TableHeader.vue'
import TableAction from '@/layouts/components/custom/TableAction.vue'

export default {
  components: {
    BAlert,
    BButton,
    BSkeletonTable,
    BCard,
    BTable,
    BModal,
    BFormCheckbox,
    BBadge,
    BImg,
    vSelect,
    Pagination,
    TableHeader,
    TableAction,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      tableFields: [
        { key: 'thumbnail', label: 'Thumbnail' },
        { key: 'title', label: 'Judul' },
        { key: 'category', label: 'Kategori' },
        { key: 'publish', label: 'Publikasi' },
        { key: 'created_at', label: 'Dibuat' },
        { key: 'actions', label: '', thStyle: { width: '1%' } },
      ],
      categoryId: [],
      isPending: false,
      isDeleting: false,
      deletedData: '',
      fixedSearchKeyword: '',
    }
  },
  computed: {
    ...mapState('article', ['articles', 'isLoading']),
    ...mapState('articleCategory', ['articleCategories']),
    categoryFilter() {
      if (this.categoryId.length) {
        return this.categoryId.map(el => el.id)
      }
      return null
    },
    query() {
      return this.$route.query
    },
    tableAction() {
      return [
        {
          access: null,
          to: null,
          action: data => { this.toExternalLink(`/artikel/${data.slug}`) },
          icon: 'EyeIcon',
          title: 'Lihat Artikel',
        },
        {
          access: this.canAccess('manage', 'Article'),
          to: 'articles-edit|id:id',
          action: '',
          icon: 'EditIcon',
          title: 'Edit',
        },
        {
          access: this.canAccess('manage', 'Article'),
          to: null,
          action: data => { this.deleteConfirmation(data.id) },
          icon: 'TrashIcon',
          title: 'Hapus',
        },
      ]
    },
  },
  async created() {
    this.getCategoriesArticle({ limit: 50 })
    await this.getArticles(this.query)
  },
  methods: {
    ...mapActions('articleCategory', ['getCategoriesArticle']),
    ...mapActions('article', ['getArticles', 'deleteArticle', 'updateArticle']),
    async handleFilterCategory() {
      this.$router.push({ query: { ...this.query, categoryId: this.categoryFilter, page: 1 } })
      await this.getArticles({ keyword: this.fixedSearchKeyword, categoryId: this.categoryFilter })
    },
    deleteConfirmation(value) {
      this.$bvModal.show('modal-delete')
      this.deletedData = value
    },
    async handleDelete() {
      this.isDeleting = true
      await this.deleteArticle({ articleId: this.deletedData })
      this.isDeleting = false
      this.$bvModal.hide('modal-delete')
    },
    activated(value, $event) {
      this.updateArticle({
        articleId: value.id,
        data: {
          ...value,
          article_category_id: value.category.map(el => el.id),
          is_active: $event ? 1 : 0,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style lang="scss" scoped>
.min-content-html {
  max-height: 25px;
  overflow: hidden;
}
</style>
